import { useState, useEffect, useCallback } from 'react';
import {
  Activity, ShieldCheck, Users, RefreshCw, AlertTriangle,
  CheckCircle, XCircle, Server, Database, Clock, User, Shield
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getToken = () => {
  try {
    return JSON.parse(localStorage.getItem('gb-sylt-user') || '{}').token || null;
  } catch {
    return null;
  }
};

// ─── Status-Badge ────────────────────────────────────────────────────────────
const StatusBadge = ({ ok, labelOk = 'OK', labelFail = 'FEHLER' }) => (
  <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
    ok ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
  }`}>
    {ok ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
    {ok ? labelOk : labelFail}
  </span>
);

// ─── Section Card ─────────────────────────────────────────────────────────────
const Card = ({ icon: Icon, title, children, accent = '#0E4C92', badge }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
    <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100" style={{ borderLeftColor: accent, borderLeftWidth: 4 }}>
      <div className="p-2 rounded-lg" style={{ backgroundColor: `${accent}15` }}>
        <Icon className="h-5 w-5" style={{ color: accent }} />
      </div>
      <h2 className="font-semibold text-gray-800 text-sm flex-1">{title}</h2>
      {badge}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

// ─── Row ─────────────────────────────────────────────────────────────────────
const Row = ({ label, value, highlight }) => (
  <div className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
    <span className="text-xs text-gray-500">{label}</span>
    <span className={`text-xs font-mono font-medium ${highlight ? 'text-red-600' : 'text-gray-700'}`}>
      {value ?? '—'}
    </span>
  </div>
);

// ─── Role Chip ────────────────────────────────────────────────────────────────
const RoleChip = ({ role }) => {
  const isAdmin = role === 'admin';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
      isAdmin ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
    }`}>
      {isAdmin ? <Shield className="h-3 w-3" /> : <User className="h-3 w-3" />}
      {isAdmin ? 'Admin' : 'Mitarbeiter'}
    </span>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
export const SystemStatusPage = ({ user }) => {
  const [health, setHealth] = useState(null);
  const [usersCheck, setUsersCheck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const token = getToken();

    try {
      const [healthRes, usersRes] = await Promise.all([
        axios.get(`${API}/health`),
        axios.get(`${API}/admin/debug/users-check`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setHealth(healthRes.data);
      setUsersCheck(usersRes.data);
      setLastRefresh(new Date());
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const missingUsers = usersCheck?.missing_required_users ?? [];
  const deployReady = usersCheck?.deploy_ready ?? false;
  const dbOk = health?.db_status === 'connected';
  const systemReady = health?.ready ?? false;

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">

        {/* ── Header ── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">System-Status</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Deployment-Readiness & Benutzer-Prüfung
            </p>
          </div>
          <button
            data-testid="refresh-btn"
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-opacity disabled:opacity-50"
            style={{ backgroundColor: '#0E4C92' }}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </button>
        </div>

        {/* ── Fehler-Banner ── */}
        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <AlertTriangle className="h-5 w-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* ── Letztes Update ── */}
        {lastRefresh && (
          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <Clock className="h-3.5 w-3.5" />
            Zuletzt aktualisiert: {lastRefresh.toLocaleTimeString('de-DE')}
          </div>
        )}

        {/* ── Übersichts-Kacheln ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            {
              label: 'System',
              ok: systemReady,
              icon: Activity,
              okText: 'Bereit',
              failText: 'Nicht bereit',
              testid: 'tile-system'
            },
            {
              label: 'Datenbank',
              ok: dbOk,
              icon: Database,
              okText: 'Verbunden',
              failText: 'Fehler',
              testid: 'tile-db'
            },
            {
              label: 'Deploy-Gate',
              ok: deployReady,
              icon: ShieldCheck,
              okText: 'Freigegeben',
              failText: 'Blockiert',
              testid: 'tile-deploy'
            },
            {
              label: 'Pflicht-User',
              ok: missingUsers.length === 0,
              icon: Users,
              okText: 'Vollständig',
              failText: `${missingUsers.length} fehlen`,
              testid: 'tile-required-users'
            }
          ].map(({ label, ok, icon: Icon, okText, failText, testid }) => (
            <div
              key={label}
              data-testid={testid}
              className={`rounded-xl p-4 border ${ok
                ? 'bg-green-50 border-green-100'
                : 'bg-red-50 border-red-100'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`h-4 w-4 ${ok ? 'text-green-600' : 'text-red-600'}`} />
                <span className="text-xs font-medium text-gray-500">{label}</span>
              </div>
              <p className={`text-sm font-semibold ${ok ? 'text-green-700' : 'text-red-700'}`}>
                {ok ? okText : failText}
              </p>
            </div>
          ))}
        </div>

        {/* ── System Health ── */}
        <Card
          icon={Activity}
          title="System Health"
          badge={<StatusBadge ok={systemReady} labelOk="Ready" labelFail="Not Ready" />}
        >
          {health ? (
            <div className="space-y-0">
              <Row label="Status" value={health.status} />
              <Row label="Version" value={health.version} />
              <Row label="Umgebung" value={health.environment} />
              <Row label="Datenbank" value={health.db_name} />
              <Row label="DB-Status" value={health.db_status} highlight={health.db_status !== 'connected'} />
              {health.deploy_warning && (
                <Row label="Deploy-Warnung" value={health.deploy_warning} highlight />
              )}
              {health.auth_error && (
                <Row label="Auth-Fehler" value={health.auth_error} highlight />
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-400">Lädt…</p>
          )}
        </Card>

        {/* ── Deploy-Gate ── */}
        <Card
          icon={ShieldCheck}
          title="Deploy-Gate"
          accent={deployReady ? '#16a34a' : '#dc2626'}
          badge={<StatusBadge ok={deployReady} labelOk="Freigegeben" labelFail="Blockiert" />}
        >
          {usersCheck ? (
            <>
              <div className="space-y-0 mb-4">
                <Row label="Datenbank" value={usersCheck.db_name} />
                <Row label="Gesamt-User" value={usersCheck.user_count} />
                <Row label="Admins" value={usersCheck.admin_count} highlight={usersCheck.admin_count === 0} />
                <Row label="Mitarbeiter" value={usersCheck.employee_count} highlight={usersCheck.employee_count === 0} />
                <Row label="Pflicht-User konfiguriert" value={usersCheck.required_users?.join(', ')} />
              </div>

              {/* Blocker */}
              {usersCheck.deploy_blockers?.length > 0 && (
                <div className={`rounded-lg p-3 ${deployReady ? 'bg-green-50' : 'bg-red-50'}`}>
                  <p className={`text-xs font-semibold mb-1 ${deployReady ? 'text-green-700' : 'text-red-700'}`}>
                    {deployReady ? 'Statusmeldungen' : 'Blocker'}
                  </p>
                  <ul className="space-y-1">
                    {usersCheck.deploy_blockers.map((b, i) => (
                      <li key={i} className={`text-xs flex items-start gap-1.5 ${deployReady ? 'text-green-600' : 'text-red-600'}`}>
                        {deployReady
                          ? <CheckCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                          : <XCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        }
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-400">Lädt…</p>
          )}
        </Card>

        {/* ── Benutzerliste ── */}
        <Card icon={Users} title="Benutzerliste">
          {usersCheck?.users ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-400 uppercase tracking-wider">
                    <th className="text-left pb-3 font-medium">Benutzername</th>
                    <th className="text-left pb-3 font-medium">Rolle</th>
                    <th className="text-left pb-3 font-medium">Aktiv</th>
                    <th className="text-left pb-3 font-medium">Normalisiert</th>
                    <th className="text-left pb-3 font-medium">Pflicht-User</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {usersCheck.users.map((u) => {
                    const isRequired = usersCheck.required_users?.some(
                      r => r.toLowerCase() === u.username?.toLowerCase()
                    );
                    return (
                      <tr key={u.username} data-testid={`user-row-${u.username}`}>
                        <td className="py-2.5 pr-4">
                          <span className="font-mono text-gray-800 text-xs">{u.username}</span>
                        </td>
                        <td className="py-2.5 pr-4">
                          <RoleChip role={u.role} />
                        </td>
                        <td className="py-2.5 pr-4">
                          <StatusBadge ok={u.is_active !== false} labelOk="Ja" labelFail="Nein" />
                        </td>
                        <td className="py-2.5 pr-4">
                          <StatusBadge ok={u.has_username_normalized} labelOk="Ja" labelFail="Nein" />
                        </td>
                        <td className="py-2.5">
                          {isRequired ? (
                            <span className="inline-flex items-center gap-1 text-xs text-blue-600 font-medium">
                              <ShieldCheck className="h-3 w-3" /> Pflicht
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-400">Lädt…</p>
          )}
        </Card>

        {/* ── Version & Sync-Hinweis ── */}
        <Card icon={Server} title="Version & Sync">
          <div className="space-y-0 mb-4">
            <Row label="Backend-Version" value={health?.version} />
            <Row label="Angemeldet als" value={`${user?.name} (${user?.role})`} />
          </div>
          <div className="p-3 bg-blue-50 rounded-lg text-xs text-blue-700 space-y-1">
            <p className="font-semibold">User-Synchronisation aus Production:</p>
            <p>Endpunkt: <code className="font-mono bg-blue-100 px-1 rounded">POST /api/admin/sync-users-from-production</code></p>
            <p>Voraussetzung: <code className="font-mono bg-blue-100 px-1 rounded">PROD_MONGO_URL</code> in Backend-ENV setzen</p>
          </div>
        </Card>

      </div>
    </div>
  );
};
